import { useEffect, useRef, type ReactNode } from "react";
import { ClipboardPen, Phone, X } from "lucide-react";
import type { Case } from "@/lib/useRealtimeCases";
import {
  BUDGET_TAX_LABELS,
  DRINKS_LABELS,
  SPECIAL_SERVICES,
  buildCallSummary,
  euros,
  getCallScriptContext,
  type BudgetTaxBasis,
  type CallGuide,
  type Drinks,
  type SpecialService,
} from "@/lib/callScript";
import {
  parisDateTimeToUtcIso,
  toParisDateInputValue,
  toParisTimeInputValue,
} from "@/lib/parisDateTime";
import { MultiSelectDropdown } from "../ui/MultiSelectDropdown";
import { ACTIVITIES_OPTIONS } from "@/lib/caseOptions";

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none";
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}
function Say({ children, yes }: { children: ReactNode; yes?: string }) {
  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 text-sm leading-relaxed text-gray-800">
      <p>« {children} »</p>
      {yes && (
        <p className="mt-1.5 text-xs font-semibold text-emerald-700">
          <span aria-hidden="true">→ </span>
          {yes}
        </p>
      )}
    </div>
  );
}
function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-4 border-t border-gray-200 pt-6 first-of-type:!mt-0 first-of-type:border-t-0 first-of-type:pt-0">
      <h3 id={id} className="font-semibold text-gray-900">
        {title}
      </h3>
      {children}
    </section>
  );
}
/** Choix unique en pastilles (HT / TTC, boissons). Recliquer sur le choix actif le retire. */
function ChoicePills<T extends string>({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string;
  name: string;
  options: readonly (readonly [T, string])[];
  value: T | null;
  onChange: (value: T | null) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-medium text-gray-700">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([option, text]) => (
          <label
            key={option}
            className={`cursor-pointer rounded-full border px-3 py-1 text-sm ${value === option ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"} has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indigo-500`}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              onClick={() => value === option && onChange(null)}
              className="sr-only"
            />
            {text}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function CallScriptDrawer({
  caseData,
  salesperson,
  quoteAmount,
  guide,
  onGuideChange,
  onFieldsChange,
  onClose,
  onNotes,
  onRecap,
  recapBusy,
  recapMessage,
  loading,
  error,
  disabled = false,
  onRetry,
}: {
  caseData: Case;
  salesperson: string;
  quoteAmount: number | null;
  guide: CallGuide;
  onGuideChange: (patch: Partial<CallGuide>) => void;
  onFieldsChange: (patch: Partial<Case>, key: string) => void;
  onClose: () => void;
  onNotes: () => void;
  onRecap: () => void;
  recapBusy: boolean;
  recapMessage: string;
  loading: boolean;
  error: boolean;
  disabled?: boolean;
  onRetry: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const context = getCallScriptContext(caseData, salesperson);
  const summary = buildCallSummary(caseData, guide);
  const services = guide.special_services || [];
  const toggleService = (service: SpecialService) => {
    const has = services.includes(service);
    const next: SpecialService[] =
      service === "none"
        ? has ? [] : ["none"]
        : has ? services.filter((s) => s !== service) : [...services.filter((s) => s !== "none"), service];
    onGuideChange({ special_services: next });
  };
  const eventTime = caseData.event_time_provided === false ? "" : toParisTimeInputValue(caseData.event_date);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  const setCase = (patch: Partial<Case>) => onFieldsChange(patch, "call-script");
  const setDate = (date: string) =>
    setCase(
      date
        ? {
            event_date: parisDateTimeToUtcIso(
              date,
              toParisTimeInputValue(caseData.event_date) || "00:00",
            ),
          }
        : { event_date: null, event_time_provided: false },
    );
  return (
    <aside
      id="case-call-script"
      aria-labelledby="call-script-title"
      className="case-call-script pointer-events-auto flex min-h-0 flex-col border-r border-gray-200 bg-white shadow-xl"
    >
      <div className="shrink-0 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h2
            ref={heading}
            tabIndex={-1}
            id="call-script-title"
            className="text-base font-semibold text-gray-900 outline-none"
          >
            Script d’appel
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le script"
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>
        <p className="truncate text-xs text-gray-500">
          {caseData.client_first_name || caseData.client_last_name
            ? `${caseData.client_first_name || ""} ${caseData.client_last_name || ""}`.trim()
            : "Contact à renseigner"}
          {caseData.company_name ? ` · ${caseData.company_name}` : ""}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {caseData.client_phone && (
            <a
              href={`tel:${caseData.client_phone.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:underline"
            >
              <Phone size={13} />
              {caseData.client_phone}
            </a>
          )}
          {quoteAmount != null && (
            <span
              className={`rounded px-2 py-1 text-xs ${quoteAmount > 1500 ? "bg-amber-100 text-amber-900" : "bg-gray-100 text-gray-600"}`}
            >
              {quoteAmount > 1500 ? "Appel à prioriser · " : "Devis · "}
              {euros(quoteAmount)} HT
            </span>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {loading && (
          <p role="status" className="mb-3 text-sm text-gray-500">
            Chargement du suivi d’appel…
          </p>
        )}
        {error && (
          <div role="alert" className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            Le suivi d’appel n’a pas pu être chargé.{" "}
            <button type="button" onClick={onRetry} className="font-medium underline">
              Réessayer
            </button>
          </div>
        )}
        <fieldset
          disabled={loading || error || disabled}
          className="min-w-0 space-y-8 disabled:opacity-60"
        >
          <legend className="sr-only">Guide d’appel</legend>
          <Section id="script-introduction" title="1. Accroche et validation de la demande">
            <Say>{context.greeting}</Say>
            <Say yes="Oui 1">{context.availability}</Say>
            <Say yes="Oui 2">{context.confirmation}</Say>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date de l’événement">
                <input
                  type="date"
                  value={toParisDateInputValue(caseData.event_date)}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Heure (Paris)">
                <input
                  type="time"
                  value={eventTime}
                  disabled={!caseData.event_date}
                  onChange={(e) => {
                    const date = toParisDateInputValue(caseData.event_date);
                    if (date)
                      setCase({
                        event_date: parisDateTimeToUtcIso(date, e.target.value || "00:00"),
                        event_time_provided: Boolean(e.target.value),
                      });
                  }}
                  className={inputClass}
                />
              </Field>
              <Field label="Participants">
                <input
                  aria-label="Participants dans le script"
                  type="number"
                  min="0"
                  step="1"
                  value={context.count ?? ""}
                  onChange={(e) => {
                    const count = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                    setCase({ participants: count, pax: count });
                  }}
                  className={inputClass}
                />
              </Field>
            </div>
            <Say yes="Oui 3 : accord pour poursuivre">{context.start}</Say>
            <p className="text-xs leading-relaxed text-gray-500">
              Pas besoin de chercher trois « oui » artificiellement : la date, l’heure et le nombre de
              participants en donnent déjà naturellement. Le troisième sert à obtenir l’accord pour
              continuer.
            </p>
            <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={guide.three_yes_validated}
                onChange={(e) => onGuideChange({ three_yes_validated: e.target.checked })}
                className="mt-0.5 accent-indigo-600"
              />
              3 oui validés
            </label>
          </Section>
          <Section id="script-budget" title="2. Budget">
            <Say>{context.budgetQuestion}</Say>
            {context.budgetFollowUp && <Say>{context.budgetFollowUp}</Say>}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Budget total (€)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={caseData.budget ?? ""}
                  onChange={(e) =>
                    setCase({ budget: e.target.value ? Math.max(0, Number(e.target.value)) : null })
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Par participant (€)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={caseData.budget_per_person ?? ""}
                  onChange={(e) =>
                    setCase({
                      budget_per_person: e.target.value
                        ? Math.max(0, parseInt(e.target.value, 10))
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </Field>
            </div>
            <ChoicePills<BudgetTaxBasis>
              label="Montant"
              name="call-budget-tax"
              options={Object.entries(BUDGET_TAX_LABELS) as [BudgetTaxBasis, string][]}
              value={guide.budget_tax_basis}
              onChange={(budget_tax_basis) => onGuideChange({ budget_tax_basis })}
            />
          </Section>
          <Section id="script-project" title="3. Découverte du projet">
            <Say>{context.discovery}</Say>
            <Field label="Objectif de l’événement">
              <textarea
                rows={3}
                value={guide.event_goal || ""}
                onChange={(e) => onGuideChange({ event_goal: e.target.value || null })}
                maxLength={4000}
                className={inputClass}
              />
            </Field>
          </Section>
          <Section id="script-activities" title="4. Activités souhaitées">
            <Say>{context.activitiesQuestion}</Say>
            <MultiSelectDropdown
              label="Activités souhaitées"
              value={caseData.activities || []}
              options={ACTIVITIES_OPTIONS}
              onChange={(activities) => setCase({ activities })}
            />
          </Section>
          <Section id="script-catering" title="5. Restauration et boissons">
            <Say>{context.cateringQuestion}</Say>
            <Field label="Restauration">
              <input
                value={guide.catering || ""}
                maxLength={1000}
                placeholder="Ex. planches apéritives, buffet, à recommander…"
                onChange={(e) => onGuideChange({ catering: e.target.value || null })}
                className={inputClass}
              />
            </Field>
            <Say>{context.drinksQuestion}</Say>
            <ChoicePills<Drinks>
              label="Boissons"
              name="call-drinks"
              options={Object.entries(DRINKS_LABELS) as [Drinks, string][]}
              value={guide.drinks}
              onChange={(drinks) => onGuideChange({ drinks })}
            />
            <Field label="Précisions (allergies, horaires, quantités…)">
              <textarea
                rows={2}
                value={guide.catering_notes || ""}
                maxLength={12000}
                onChange={(e) => onGuideChange({ catering_notes: e.target.value || null })}
                className={inputClass}
              />
            </Field>
          </Section>
          <Section id="script-extras" title="6. Extras / prestations particulières">
            <Say>{context.extrasQuestion}</Say>
            <fieldset>
              <legend className="mb-1.5 text-sm font-medium text-gray-700">Prestations particulières</legend>
              <div className="flex flex-wrap gap-1.5">
                {SPECIAL_SERVICES.map(([service, text]) => {
                  const on = services.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleService(service)}
                      className={`rounded-full border px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}
                    >
                      {text}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <Field label="Précisions sur les prestations">
              <textarea
                rows={2}
                value={guide.special_services_notes || ""}
                maxLength={4000}
                onChange={(e) => onGuideChange({ special_services_notes: e.target.value || null })}
                className={inputClass}
              />
            </Field>
          </Section>
          <Section id="script-follow-up" title="7. Reformulation et fin d’appel">
            <Say>{summary}</Say>
            <Say>{context.proposal}</Say>
            <Say>{context.followUp}</Say>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date de relance">
                <input
                  type="date"
                  value={caseData.phone_reminder_date || ""}
                  onChange={(e) =>
                    setCase(
                      e.target.value
                        ? { phone_reminder_date: e.target.value }
                        : { phone_reminder_date: null, phone_reminder_time: null },
                    )
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Heure (Paris)">
                <input
                  type="time"
                  value={caseData.phone_reminder_time?.slice(0, 5) || ""}
                  disabled={!caseData.phone_reminder_date}
                  onChange={(e) => setCase({ phone_reminder_time: e.target.value || null })}
                  className={inputClass}
                />
              </Field>
            </div>
            <p className="text-xs text-gray-500">
              L’heure est le rendez-vous convenu avec le client, sans message automatique.
            </p>
            {(!guide.three_yes_validated ||
              !caseData.phone_reminder_date ||
              !caseData.phone_reminder_time) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">À compléter avant de terminer l’appel</p>
                <ul className="mt-1 list-disc pl-4">
                  {!guide.three_yes_validated && <li>Valider les 3 oui.</li>}
                  {(!caseData.phone_reminder_date || !caseData.phone_reminder_time) && (
                    <li>Convenir d’une date et d’une heure de relance.</li>
                  )}
                </ul>
              </div>
            )}
          </Section>
        </fieldset>
      </div>
      <div className="shrink-0 space-y-2 border-t border-gray-200 bg-white px-4 py-3">
        <button type="button" onClick={onRecap} disabled={disabled || loading || error || recapBusy}
          className="w-full rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {recapBusy ? "Enregistrement du récapitulatif…" : "Ajouter le récap aux notes internes"}
        </button>
        {recapMessage && <p role="status" className="text-xs text-gray-700">{recapMessage}</p>}
        <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onNotes}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ClipboardPen size={16} />
          Notes et statut du dossier
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-2 text-sm text-gray-600 hover:bg-gray-100"
        >
          Replier
        </button>
        </div>
      </div>
    </aside>
  );
}
